import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Typography,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Alert,
  Card,
  CardHeader,
  CardContent,
  TableContainer,
  Paper,
  Grid,
  Tooltip,
} from '@mui/material';
import api from '../services/api';

export default function DashboardPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [errorMsg, setErrorMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);
  const [migrationMsg, setMigrationMsg] = useState('');

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data);
      setErrorMsg('');
    } catch (error) {
      console.error(error);
      setErrorMsg('Failed to fetch users. Check if you are logged in or have proper permissions.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleUpdateAccess = (username) => {
    navigate(`/update-access/${username}`);
  };

  const handleCreateUser = () => {
    navigate('/create-user');
  };

  // Activate Thread handler
  const handleActivateThread = async (username) => {
    try {
      const response = await api.post(`/admin/activate-thread/${username}`);
      // The backend typically returns something like:
      // { message: "Thread activated", active_filename: "somefile.json" }
      setErrorMsg(
        response.data.message +
          (response.data.active_filename ? ` (Active: ${response.data.active_filename})` : '')
      );
      // Optionally refresh user list:
      fetchUsers();
    } catch (error) {
      console.error(error);
      setErrorMsg(
        `Failed to activate thread: ${error.response?.data?.detail || error.message}`
      );
    }
  };

  // Migrate Common Pool handler
  const handleMigrateCommonPool = async () => {
    if (!window.confirm('This will clear existing common pool entries and rebuild from fannotator user assignments. Continue?')) {
      return;
    }
    setIsMigrating(true);
    setMigrationMsg('');
    try {
      const response = await api.post('/admin/migrate-common-pool');
      setMigrationMsg(response.data.message || 'Common pool migration completed successfully.');
      // Refresh user list to reflect any changes
      fetchUsers();
    } catch (error) {
      console.error(error);
      setMigrationMsg(`Migration failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsMigrating(false);
    }
  };

  return (
    <Box p={3}>
      <Grid container spacing={3}>
        {/* Dashboard Header */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title={<Typography variant="h4">Admin Dashboard</Typography>}
              action={
                <Box>
                  <Button variant="contained" onClick={handleCreateUser} sx={{ mr: 1 }}>
                    Create New User
                  </Button>
                  <Button variant="contained" onClick={() => navigate('/ai-annotations')} sx={{ mr: 1 }}>
                    AI Annotation Admin
                  </Button>
                  <Button variant="contained" onClick={() => navigate('/fannotator-files')} sx={{ mr: 1 }}>
                    Fannotator Files
                  </Button>
                  <Button variant="contained" onClick={() => navigate('/ai-annotation-check')} sx={{ mr: 1 }}>
                    AI Annotation Check
                  </Button>
                  <Tooltip title="Clear and rebuild common pool from fannotator user assignments">
                    <Button variant="contained" color="warning" onClick={handleMigrateCommonPool} disabled={isMigrating}>
                      {isMigrating ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                      {isMigrating ? 'Migrating...' : 'Migrate Common Pool'}
                    </Button>
                  </Tooltip>
                </Box>
              }
            />
          </Card>
        </Grid>

        {/* Main Content */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              {errorMsg && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errorMsg}
                </Alert>
              )}
              {migrationMsg && (
                <Alert severity={migrationMsg.toLowerCase().includes('failed') ? 'error' : 'info'} sx={{ mb: 2 }}>
                  {migrationMsg}
                </Alert>
              )}
              {isLoading ? (
                <Box display="flex" justifyContent="center" my={4}>
                  <CircularProgress />
                </Box>
              ) : users.length > 0 ? (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Username</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Is Admin</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.map((user) => (
                        <TableRow key={user.username}>
                          <TableCell>{user.username}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>{user.is_admin ? 'Yes' : 'No'}</TableCell>
                          <TableCell align="center">
                            <Button
                              variant="outlined"
                              onClick={() => handleUpdateAccess(user.username)}
                              size="small"
                            >
                              Update Access
                            </Button>
                            <Button
                              variant="contained"
                              color="secondary"
                              onClick={() => handleActivateThread(user.username)}
                              sx={{ ml: 1 }}
                              size="small"
                            >
                              Activate Thread
                            </Button>
                            <Button
                              variant="text"
                              color="primary"
                              onClick={() => navigate(`/user-details/${user.username}`)}
                              sx={{ ml: 1 }}
                              size="small"
                            >
                              View Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="subtitle1">No users available.</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
